import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Shell } from '@/components/Shell';
import { WorkspaceGate } from '@/components/WorkspaceGate';
import { useAuth } from '@/auth/AuthContext';
import { isFirebaseEnabled } from '@/auth/firebase';
// SITE_URL is still referenced for any explicit marketing-site CTAs we want to
// route back to (e.g., /contact-sales), but / no longer auto-redirects there —
// the app subdomain should always boot the SPA, not bounce users away.
import SignInPage from '@/routes/auth/SignInPage';
import SignUpPage from '@/routes/auth/SignUpPage';
import DashboardRoute from '@/routes/dashboard';
import DraftsRoute from '@/routes/drafts';
import AutomationRoute from '@/routes/automation';
import BrandRoute from '@/routes/brand';
import ConnectionsRoute from '@/routes/connections';
import ProfileRoute from '@/routes/profile';
import CreateRoute from '@/routes/create';
import ReviewRoute from '@/routes/review';
import AnalyticsRoute from '@/routes/analytics';
import InboxRoute from '@/routes/inbox';
import BillingRoute from '@/routes/billing';
import CalendarRoute from '@/routes/calendar';
import ContentRoute from '@/routes/content';
import MediaRoute from '@/routes/media';
import TemplatesRoute from '@/routes/templates';
import SourcesRoute from '@/routes/sources';
import IntegrationsRoute from '@/routes/integrations';
import SettingsRoute from '@/routes/settings';

// Marketing site origin (Hostinger in prod, localhost:5181 in dev).
const SITE_URL = (import.meta.env.VITE_SITE_URL ?? (import.meta.env.DEV ? 'http://localhost:5181' : '')).replace(/\/$/, '');

/** Hard-redirects the browser to the marketing site. */
function ToSite({ path = '/' }: { path?: string }) {
  if (typeof window !== 'undefined') location.replace(`${SITE_URL}${path}`);
  return null;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (!isFirebaseEnabled) return <>{children}</>;       // dev/mock — don't gate
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (!user) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/auth/sign-in?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** Auth pages should never be reachable for signed-in users. Pressing back
 * from /app/dashboard would otherwise show the sign-up form again. */
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (!isFirebaseEnabled) return <>{children}</>;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return <>{children}</>;
}

/** Root landing: send authed users to dashboard, unauthed to sign-in. */
function RootRedirect() {
  const { user, loading } = useAuth();
  if (!isFirebaseEnabled) return <Navigate to="/app/dashboard" replace />;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  return user
    ? <Navigate to="/app/dashboard" replace />
    : <Navigate to="/auth/sign-in" replace />;
}

export default function App() {
  return (
    <Routes>
      {/* Auth pages live on the app (same origin as /app/*) — no more
          cross-origin token handoff. PublicOnlyRoute kicks signed-in users
          to /app/dashboard so back-button from dashboard doesn't show
          the sign-up form again. */}
      <Route path="/auth/sign-in" element={<PublicOnlyRoute><SignInPage /></PublicOnlyRoute>} />
      <Route path="/auth/sign-up" element={<PublicOnlyRoute><SignUpPage /></PublicOnlyRoute>} />

      {/* Root: route by auth state (dashboard if signed in, sign-in otherwise).
          The app subdomain should never bounce users to the marketing site. */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/contact-sales" element={<ToSite path="/contact-sales" />} />

      <Route path="/app" element={<ProtectedRoute><WorkspaceGate><Shell /></WorkspaceGate></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardRoute />} />
        <Route path="inbox" element={<InboxRoute />} />
        <Route path="analytics" element={<AnalyticsRoute />} />
        <Route path="calendar" element={<CalendarRoute />} />
        <Route path="drafts" element={<DraftsRoute />} />
        <Route path="create" element={<CreateRoute />} />
        <Route path="content" element={<ContentRoute />} />
        <Route path="review" element={<ReviewRoute />} />
        <Route path="sources" element={<SourcesRoute />} />
        <Route path="templates" element={<TemplatesRoute />} />
        <Route path="media" element={<MediaRoute />} />
        <Route path="brand" element={<BrandRoute />} />
        <Route path="connections" element={<ConnectionsRoute />} />
        <Route path="integrations" element={<IntegrationsRoute />} />
        <Route path="billing" element={<BillingRoute />} />
        <Route path="automation" element={<AutomationRoute />} />
        <Route path="profile" element={<ProfileRoute />} />
        <Route path="settings/*" element={<SettingsRoute />} />
      </Route>

      {/* Unknown paths on the app subdomain should land in-app too, not bounce. */}
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}
